import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from "firebase/firestore";
import { signOut, User } from 'firebase/auth';

interface AccountCenterProps {
  user: User;
  onClose: () => void;
}

const AccountCenter: React.FC<AccountCenterProps> = ({ user, onClose }) => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user) return;

      setLoadingBookings(true);
      setError(null);
      try {
        const q = query(
          collection(db, "bookings"),
          where("userId", "==", user.uid)
        );
        const querySnapshot = await getDocs(q);
        const fetchedBookings = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setBookings(fetchedBookings);
      } catch (err: any) {
        console.error("Error fetching bookings:", err);
        setError("Could not retrieve booking history. Please try again.");
      } finally {
        setLoadingBookings(false);
      }
    };

    fetchBookings();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      onClose(); // Close the modal after signing out
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Failed to sign out. Please try again.");
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Account Center</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <p><strong>Signed in as:</strong> {user.email}</p>
            {user.displayName && <p><strong>Full Name:</strong> {user.displayName}</p>}
            <hr />
            <h6>Your Booking History</h6>
            {loadingBookings && <p>Loading bookings...</p>}
            {error && <p className="text-danger">{error}</p>}
            {!loadingBookings && bookings.length === 0 && !error && <p>No bookings found.</p>}
            {!loadingBookings && bookings.length > 0 && (
              <ul className="list-group">
                {bookings.map((booking) => (
                  <li key={booking.id} className="list-group-item">
                    <strong>Service:</strong> {booking.service}<br />
                    <strong>Date:</strong> {new Date(booking.preferredDate).toLocaleDateString()}<br />
                    {booking.amount > 0 && (
                      <><strong>Amount:</strong> ${(booking.amount / 100).toFixed(2)} {booking.currency.toUpperCase()}<br /></>
                    )}
                    <small className="text-muted">Booked on: {booking.submittedAt?.toDate().toLocaleString()}</small>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={handleSignOut}>Sign Out</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCenter;
